/**
 * Data layer for the Lawn Irrigation Dashboard.
 *
 * Components only ever call the functions in this file, never `fetch` directly.
 * Each function tries the `/api/lawn/*` endpoints first and falls back to
 * localStorage plus mock data when the API is unreachable, so the concept stays
 * demonstrable before the backend is deployed.
 *
 * When the lawn product moves to its own service, only this file and
 * `src/utils/lawnApi.js` need to change.
 */

import * as lawnApi from '../utils/lawnApi';
import {
  buildMockDashboard,
  buildMockForecast,
  buildMockReadings,
} from '../mocks/lawnMocks';
import { GRID_COLUMNS, SENSOR_STATUSES, SENSOR_TYPES } from '../types/lawnTypes';

const STORAGE_PREFIX = 'lawnDashboard';

/** Where the last successful read came from, so the UI can label demo data. */
export const DATA_SOURCES = {
  api: 'api',
  local: 'local',
};

const storageKey = (username) => `${STORAGE_PREFIX}:${username || 'anonymous'}`;

const readLocal = (username) => {
  try {
    const raw = localStorage.getItem(storageKey(username));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('lawnDataLayer: could not read local dashboard', error);
    return null;
  }
};

const writeLocal = (username, data) => {
  try {
    localStorage.setItem(storageKey(username), JSON.stringify(data));
  } catch (error) {
    console.warn('lawnDataLayer: could not persist local dashboard', error);
  }
};

export const clearLocal = (username) => {
  try {
    localStorage.removeItem(storageKey(username));
  } catch (error) {
    console.warn('lawnDataLayer: could not clear local dashboard', error);
  }
};

const newLocalId = (prefix) =>
  `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const clampPct = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(100, Math.round(numeric)));
};

const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

/** Mirror of the backend rule: explicit status wins, else infer from battery. */
const deriveStatus = (draft) => {
  if (SENSOR_STATUSES.includes(draft.status)) return draft.status;
  return clampPct(draft.batteryPct, 100) <= 20 ? 'low_battery' : 'online';
};

const normalizeSensor = (draft, propertyId) => ({
  id: draft.id || newLocalId('sen'),
  propertyId,
  name: (draft.name || '').trim() || 'New sensor',
  type: oneOf(draft.type, SENSOR_TYPES, 'soil_moisture'),
  xPct: clampPct(draft.xPct, 50),
  yPct: clampPct(draft.yPct, 50),
  batteryPct: clampPct(draft.batteryPct, 100),
  moisturePct:
    draft.moisturePct === null || draft.moisturePct === undefined || draft.moisturePct === ''
      ? null
      : clampPct(draft.moisturePct, 0),
  status: deriveStatus(draft),
  lastReadingAt: draft.lastReadingAt || new Date().toISOString(),
});

/** Keep widget geometry inside the 12-column grid regardless of source. */
const normalizeWidget = (widget) => {
  const width = Math.max(2, Math.min(GRID_COLUMNS, Number(widget.width) || 4));
  const x = Math.max(0, Math.min(GRID_COLUMNS - width, Number(widget.x) || 0));
  return {
    id: widget.id || newLocalId('wid'),
    widgetType: widget.widgetType,
    x,
    y: Math.max(0, Number(widget.y) || 0),
    width,
    height: Math.max(2, Number(widget.height) || 4),
    configJson: typeof widget.configJson === 'string' ? widget.configJson : '{}',
  };
};

const normalizeDashboard = (data) => ({
  properties: data.properties || [],
  property: data.property || null,
  sensors: (data.sensors || []).map((sensor) =>
    normalizeSensor(sensor, sensor.propertyId || data.property?.id)
  ),
  forecast: data.forecast || [],
  ordinance: data.ordinance || null,
  widgets: (data.widgets || []).map(normalizeWidget),
});

/**
 * Load the dashboard. Falls back to the stored local copy, then to mock data.
 *
 * @returns {Promise<{data: import('../types/lawnTypes').LawnDashboardData, source: string, apiError: (string|null)}>}
 */
export async function getDashboard(username, propertyId) {
  if (username && lawnApi.isLawnApiConfigured()) {
    try {
      const payload = await lawnApi.fetchLawnDashboard(username, propertyId);
      if (payload && payload.property) {
        const data = normalizeDashboard(payload);
        writeLocal(username, data);
        return { data, source: DATA_SOURCES.api, apiError: null };
      }
    } catch (error) {
      console.warn('lawnDataLayer: lawn API unavailable, using local demo data', error);
      const stored = readLocal(username);
      const data = stored ? normalizeDashboard(stored) : normalizeDashboard(buildMockDashboard());
      if (!stored) writeLocal(username, data);
      return { data, source: DATA_SOURCES.local, apiError: error.message };
    }
  }

  const stored = readLocal(username);
  const data = stored ? normalizeDashboard(stored) : normalizeDashboard(buildMockDashboard());
  if (!stored) writeLocal(username, data);
  return { data, source: DATA_SOURCES.local, apiError: null };
}

/**
 * Create a sensor at a pinned position.
 *
 * @returns {Promise<import('../types/lawnTypes').Sensor>}
 */
export async function addSensor(username, propertyId, draft, { source } = {}) {
  if (source === DATA_SOURCES.api && username) {
    const created = await lawnApi.createLawnSensor(username, { ...draft, propertyId });
    return normalizeSensor(created, propertyId);
  }

  const sensor = normalizeSensor(draft, propertyId);
  const stored = readLocal(username) || buildMockDashboard();
  stored.sensors = [...(stored.sensors || []), sensor];
  writeLocal(username, stored);
  return sensor;
}

/**
 * Update a sensor (rename, retype, or move its pin).
 *
 * @returns {Promise<import('../types/lawnTypes').Sensor>}
 */
export async function updateSensor(username, sensorId, updates, { source, propertyId } = {}) {
  if (source === DATA_SOURCES.api && username) {
    const updated = await lawnApi.patchLawnSensor(username, sensorId, updates);
    return normalizeSensor(updated, updated.propertyId || propertyId);
  }

  const stored = readLocal(username) || buildMockDashboard();
  let result = null;
  stored.sensors = (stored.sensors || []).map((sensor) => {
    if (sensor.id !== sensorId) return sensor;
    result = normalizeSensor({ ...sensor, ...updates }, sensor.propertyId || propertyId);
    return result;
  });
  writeLocal(username, stored);
  return result;
}

export async function removeSensor(username, sensorId, { source } = {}) {
  if (source === DATA_SOURCES.api && username) {
    await lawnApi.deleteLawnSensor(username, sensorId);
    return true;
  }

  const stored = readLocal(username) || buildMockDashboard();
  stored.sensors = (stored.sensors || []).filter((sensor) => sensor.id !== sensorId);
  writeLocal(username, stored);
  return true;
}

/**
 * Recent moisture readings for the sensor detail sparkline.
 *
 * @returns {Promise<import('../types/lawnTypes').SensorReading[]>}
 */
export async function getSensorReadings(username, sensor, { source } = {}) {
  if (source === DATA_SOURCES.api && username && sensor?.id) {
    try {
      const readings = await lawnApi.fetchLawnSensorReadings(username, sensor.id);
      if (Array.isArray(readings) && readings.length) {
        return readings;
      }
    } catch (error) {
      console.warn('lawnDataLayer: falling back to derived readings', error);
    }
  }

  return buildMockReadings(sensor?.moisturePct ?? null);
}

/** @returns {Promise<import('../types/lawnTypes').ForecastDay[]>} */
export async function getForecast(username, propertyId, { source } = {}) {
  if (source === DATA_SOURCES.api && username && propertyId) {
    try {
      const forecast = await lawnApi.fetchLawnForecast(username, propertyId);
      if (Array.isArray(forecast)) return forecast;
    } catch (error) {
      console.warn('lawnDataLayer: falling back to mock forecast', error);
    }
  }
  return buildMockForecast(propertyId);
}

/** @returns {Promise<import('../types/lawnTypes').Ordinance|null>} */
export async function getOrdinance(username, propertyId, { source } = {}) {
  if (source === DATA_SOURCES.api && username && propertyId) {
    try {
      return await lawnApi.fetchLawnOrdinance(username, propertyId);
    } catch (error) {
      console.warn('lawnDataLayer: falling back to mock ordinance', error);
    }
  }
  return buildMockDashboard().ordinance;
}

/** Persist the whole grid layout. */
export async function saveWidgets(username, widgets, { source } = {}) {
  const normalized = widgets.map(normalizeWidget);

  if (source === DATA_SOURCES.api && username) {
    const saved = await lawnApi.saveLawnWidgets(username, normalized);
    return (Array.isArray(saved) ? saved : normalized).map(normalizeWidget);
  }

  const stored = readLocal(username) || buildMockDashboard();
  stored.widgets = normalized;
  writeLocal(username, stored);
  return normalized;
}

/** Persist property edits, including a locally chosen yard image. */
export async function updateProperty(username, propertyId, updates, { source } = {}) {
  if (source === DATA_SOURCES.api && username) {
    return lawnApi.patchLawnProperty(username, propertyId, updates);
  }

  const stored = readLocal(username) || buildMockDashboard();
  stored.property = { ...stored.property, ...updates };
  stored.properties = (stored.properties || []).map((item) =>
    item.id === propertyId ? { ...item, ...updates } : item
  );
  writeLocal(username, stored);
  return stored.property;
}

/**
 * Restore the original demo property, sensors, forecast, and layout.
 *
 * @returns {Promise<{data: import('../types/lawnTypes').LawnDashboardData, source: string}>}
 */
export async function resetDashboard(username, { source } = {}) {
  if (source === DATA_SOURCES.api && username) {
    const payload = await lawnApi.resetLawnDashboard(username);
    const data = normalizeDashboard(payload);
    writeLocal(username, data);
    return { data, source: DATA_SOURCES.api };
  }

  clearLocal(username);
  const data = normalizeDashboard(buildMockDashboard());
  writeLocal(username, data);
  return { data, source: DATA_SOURCES.local };
}
